import { useQuery } from '@tanstack/react-query'
import { getProcurementSuggestedImages } from 'api'
import axios from 'axios'
import { JustSpinner, Spinner } from 'components/Loading/Loading'
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from 'components/ModalV2/Modal'
import { ILineItem } from 'pages/Procurement/CreateProcurement'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { Button, Flex } from 'styling/GlobalStyles'
import { PurchaseRequestItemType } from 'types/procurement'

export const SuggestedImagesModal = ({
  onClose,
  item,
  onSave,
}: {
  onClose: () => void
  item: ILineItem
  onSave: (images: File[]) => void
}) => {
  const { data: images, isLoading: isLoadingImages } = useQuery({
    queryKey: ['suggested-images', item.id, item.name],
    queryFn: () =>
      getProcurementSuggestedImages({
        inventoryItemId:
          item.type === PurchaseRequestItemType.Item ? item.inventoryItemId ?? null : null,
        name: item.name,
      }),
  })
  const [isLoading, setIsLoading] = useState(false)
  const [selectedImages, setSelectedImages] = useState<string[]>([])
  const onImageClick = (image: string) => {
    if (selectedImages.includes(image)) {
      setSelectedImages(selectedImages.filter((img) => img !== image))
    } else {
      setSelectedImages([...selectedImages, image])
    }
  }

  const onAddImages = async () => {
    setIsLoading(true)

    try {
      const files = await Promise.all(
        selectedImages.map(async (imageUrl, index) => {
          const response = await axios.get(imageUrl, {
            responseType: 'blob',
          })

          return new File([response.data], `image-${index}.jpg`, {
            type: response.data.type || 'image/jpeg',
          })
        }),
      )

      onSave(files)
      onClose()
    } catch (error) {
      toast.error('Failed to add images')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Modal onClose={onClose}>
        <ModalContent>
          <ModalHeader>Suggested Images</ModalHeader>
          <ModalBody>
            {isLoadingImages ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '200px',
                  width: '100%',
                }}
              >
                <JustSpinner />
              </div>
            ) : (
              <Grid>
                {images?.map((image) => (
                  <Image
                    key={image}
                    src={image}
                    alt={image}
                    onClick={() => onImageClick(image)}
                    selected={selectedImages.includes(image)}
                  />
                ))}
              </Grid>
            )}
          </ModalBody>
          <ModalFooter>
            {' '}
            <Flex gap='1rem' justend width='100%'>
              <Button onClick={() => onClose()}>Close</Button>
              <Button onClick={() => onAddImages()} orange>
                Add Images
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Spinner loading={isLoading} />
    </>
  )
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
`

const Image = styled.img<{ selected: boolean }>`
  width: 200px;
  height: 200px;
  cursor: pointer;

  border-radius: 5px;

  ${(props) => props.selected && 'border: 10px solid ' + props.theme.colorOrange};
`
